import {
    useState,
    PropsWithChildren,
    useEffect,
    useEffectEvent,
    useMemo,
    useRef,
} from "react";
import { BillViewOptions, BillViewOptionGroup } from "@common/types/bill";
import { useBillsPageFiltersCache } from "@hooks/view/useBillsPageFiltersCache";
import { useBillsPageViewOptionsCache } from "@hooks/view/useBillsPageViewOptionsCache";
import { defaultBillStatuses } from "@components/pageHeader/filters/lib";
import { useBillStats } from "@api/bill.api";
import { JsonParam, StringParam, useQueryParams } from "use-query-params";
import { z } from "zod";
import {
    BillsViewContext,
    BillViewFilters,
    toBillApiFilters,
} from "./BillsViewContext";

const billUrlFiltersSchema = z.object({
    billIds: z.array(z.string()).optional(),
    entityIds: z.array(z.number()).optional(),
    counterpartiesIds: z.array(z.string()).optional(),
});

export const BillsViewProvider: React.FC<PropsWithChildren> = ({
    children,
}) => {
    const [cachedFilters, setCachedFilters] = useBillsPageFiltersCache();
    const [cachedViewOptions, setCachedViewOptions] =
        useBillsPageViewOptionsCache();

    const [
        { filters: urlFilters, scheduleTemplateId: urlScheduleTemplateId },
        setQueryParams,
    ] = useQueryParams({
        filters: JsonParam,
        scheduleTemplateId: StringParam,
    });

    const parsedUrlFilters = useMemo(() => {
        if (!urlFilters) {
            return undefined;
        }
        const { data, success } = billUrlFiltersSchema.safeParse(urlFilters);
        return success ? data : undefined;
    }, [urlFilters]);

    const initialIdsRef = useRef(parsedUrlFilters?.billIds);
    const initialIds = initialIdsRef.current;
    const hasUrlFilters = Boolean(
        parsedUrlFilters?.billIds?.length ??
        parsedUrlFilters?.entityIds?.length ??
        parsedUrlFilters?.counterpartiesIds?.length,
    );

    const [filters, setFilters] = useState<BillViewFilters | undefined>(() => {
        const cachedApiFilters = { ...cachedFilters };
        delete cachedApiFilters.billStatuses;
        if (hasUrlFilters && parsedUrlFilters) {
            return {
                externalBillStatuses: defaultBillStatuses,
                ...(parsedUrlFilters.billIds && {
                    billIds: parsedUrlFilters.billIds,
                }),
                ...(parsedUrlFilters.entityIds && {
                    entityIds: parsedUrlFilters.entityIds,
                }),
                ...(parsedUrlFilters.counterpartiesIds && {
                    counterpartiesIds: parsedUrlFilters.counterpartiesIds,
                }),
            };
        }
        return {
            externalBillStatuses: defaultBillStatuses,
            ...cachedApiFilters,
        };
    });

    const clearUrlFiltersOnMount = useEffectEvent(() => {
        if (hasUrlFilters) {
            setQueryParams({ filters: undefined }, "replaceIn");
        }
    });
    useEffect(() => {
        clearUrlFiltersOnMount();
    }, []);

    const [viewOption, setViewOption] = useState<BillViewOptions>(() => {
        // "Add bills" on a policy lands on whole bills, where its column is.
        if (urlScheduleTemplateId) {
            return { groupBy: BillViewOptionGroup.BILL };
        }
        if (initialIds?.length) {
            return {
                groupBy: BillViewOptionGroup.LINE_ITEMS,
            };
        }
        return (
            cachedViewOptions ?? {
                groupBy: BillViewOptionGroup.LINE_ITEMS,
            }
        );
    });

    useEffect(() => {
        setCachedFilters(toBillApiFilters(filters));
    }, [filters, setCachedFilters]);

    useEffect(() => {
        setCachedViewOptions(viewOption);
    }, [viewOption, setCachedViewOptions]);

    const { data: billStats } = useBillStats({
        filters: toBillApiFilters(filters),
    });

    const contextValue = useMemo(
        () => ({
            filters,
            setFilters,
            viewOption,
            setViewOption,
            billStats: billStats?.data,
            initialIds,
        }),
        [
            filters,
            setFilters,
            viewOption,
            setViewOption,
            billStats?.data,
            initialIds,
        ],
    );

    return (
        <BillsViewContext.Provider value={contextValue}>
            {children}
        </BillsViewContext.Provider>
    );
};
